import React, { useState, useEffect, createContext, useContext } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { configService } from '../services/configService';
import GoogleGsiInitializer from './auth/GoogleGsiInitializer';

const BUILD_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export const GoogleClientIdContext = createContext('');

export function useGoogleClientId() {
  return useContext(GoogleClientIdContext);
}

function isManagedProductionHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname.toLowerCase();
  return (
    h === 'contsuite.com.ar'
    || h === 'www.contsuite.com.ar'
    || h.endsWith('.vercel.app')
  );
}

/**
 * En prod no montamos GIS hasta tener el Client ID de /api/config/public.
 * Evita que un REACT_APP_GOOGLE_CLIENT_ID viejo del build dispare "origin not allowed".
 */
const AsyncGoogleOAuthProvider = ({ children }) => {
  const waitForApi = isManagedProductionHost();
  const [clientId, setClientId] = useState(waitForApi ? '' : BUILD_GOOGLE_CLIENT_ID);
  const [configReady, setConfigReady] = useState(!waitForApi);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await configService.getGoogleClientId();
        if (cancelled) return;
        if (id) {
          if (BUILD_GOOGLE_CLIENT_ID && BUILD_GOOGLE_CLIENT_ID !== id) {
            console.warn(
              '[Google OAuth] El Client ID del build no coincide con /api/config/public; se usa el del servidor.'
            );
          }
          setClientId(id);
        } else if (BUILD_GOOGLE_CLIENT_ID) {
          setClientId(BUILD_GOOGLE_CLIENT_ID);
        }
      } catch (err) {
        console.warn('AsyncGoogleOAuthProvider:', err);
        if (!cancelled && BUILD_GOOGLE_CLIENT_ID) setClientId(BUILD_GOOGLE_CLIENT_ID);
      } finally {
        if (!cancelled) setConfigReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const effectiveId = clientId || (!waitForApi ? BUILD_GOOGLE_CLIENT_ID : '');
  const providerReady = configReady && Boolean(effectiveId);

  const inner = providerReady ? (
    <GoogleOAuthProvider clientId={effectiveId} key={effectiveId}>
      <GoogleGsiInitializer />
      {children}
    </GoogleOAuthProvider>
  ) : (
    children
  );

  return (
    <GoogleClientIdContext.Provider value={providerReady ? effectiveId : ''}>
      {inner}
    </GoogleClientIdContext.Provider>
  );
};

export default AsyncGoogleOAuthProvider;
